import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Popconfirm, Table } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import { App} from 'antd';
import { Modal } from 'antd';
import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification';

import { useNavigate } from 'react-router-dom';
import AxiosCaller from '../../../authenticatie/AxiosCaller';

const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.error('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24 }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const EenheidScreen = () => {
  const { get, put, del, post } = AxiosCaller();
  const [eenheden, setEenheden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEenheidIndex, setNewEenheidIndex] = useState(null); // Voor tracking van de nieuw toegevoegde eenheid
  const tableRef = useRef(null); // Ref voor de Table component
  
  useEffect(() => {
    fetchEenheden();
  }, []);

  const fetchEenheden = async () => {
    try {
      const response = await get('/eenheid');
      setEenheden(response.data);
    } catch (error) {
      console.error('Error fetching eenheden:', error);
      openNotificationError("Fout bij ophalen van eenheden", error)
    } finally {
      setLoading(false);
    }
  };

  

  const handleDelete = async (key) => {
    try {
      await del(`/eenheid/${key}`);
      setEenheden(eenheden.filter((item) => item._id !== key));
      openNotificationSuccess("Eenheid succesvol verwijderd")
    } catch (error) {
      console.error('Error deleting eenheid:', error);
      openNotificationError("Fout bij verwijderen van eenheid", error)
    }
  };

  const handleSave = async (row) => {
    try {
      await put(`/eenheid/${row._id}`, { naam: row.naam });
      const newData = eenheden.map((item) => (item._id === row._id ? row : item));
      setEenheden(newData);
    } catch (error) {
      console.error('Error updating eenheid:', error);
      openNotificationError("Fout bij aanpassen eenheid", error)
    }
  };

  const handleAdd = async () => {
    try {
      const newEenheid = { naam: 'Nieuwe Eenheid' };
      const response = await post('/eenheid', newEenheid);
      // Voeg de nieuwe eenheid bovenaan en stel de index in
      setEenheden([response.data, ...eenheden]);
      setNewEenheidIndex(0); // Zet de index van het nieuwe product
    } catch (error) {
      console.error('Error adding eenheid:', error);
      openNotificationError('Fout bij toeveogen van eenheid', error)
    }
  };

  const columns = [
    {
      title: 'Naam',
      dataIndex: 'naam',
      editable: true,
      sorter: (a, b) => a.naam.localeCompare(b.naam),
    },
    {
      title: 'Acties',
      dataIndex: 'operation',
      width: '15%',
      render: (_, record) => (
        <Popconfirm title="Weet je het zeker?" onConfirm={() => handleDelete(record._id)}>
          <Trash2 className='delete-button'/>
        </Popconfirm>
      ),
    },
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columnConfig = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div className='Screen-container'>
      <div className='space-between'>
        <h1 className='title'>Eenheden</h1>
        <Button onClick={handleAdd} type="primary" className='button'>
          <Plus /> Eenheid toevoegen
        </Button>
      </div>

      <Table
        ref={tableRef}
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={eenheden.map((eenheid) => ({ ...eenheid, key: eenheid._id }))}
        columns={columnConfig}
        pagination={false} // Pagination verwijderd
        scroll={{ y: 0.5 * window.innerHeight}} // Scrollen wordt ondersteund
        loading={loading}
      />
    </div>
  );
};

export default EenheidScreen;
